import { apiGet, apiPost } from "../fetch";

/** Whether an assignment's source pool can still refill its LLM queue;
 *  `no_requirements` while the role lists none, so nothing can start yet. */
export type CandidatePoolState =
	| "no_requirements"
	| "ok"
	| "needs_mining"
	| "exhausted";

/** The next step "Artemis' analysis" recommends; the card turns it into its main button. */
export type DiagnosisAction =
	| "edit_requirements"
	| "review_rejected"
	| "relogin_linkedin"
	| "wait";

/** Why a role gets few candidates, in plain words, and the one next step.
 *  Worked out on the server per requirement version and pool state. */
export interface RoleDiagnosis {
	headline: string;
	explanation: string;
	action: DiagnosisAction;
	/** The must-have in question: "Review rejected" opens the rejected list
	 *  filtered to the people rejected on it alone. Null opens the whole list. */
	review_requirement_id: number | null;
}

export interface AssignmentLlmQueueStat {
	assignment_id: number;
	llm_queue_count: number;
	/** Null while the assignment's first pool state computes on the server; the next poll has it. */
	pool_state: CandidatePoolState | null;
	/** Set when the role gets few candidates or few of them match; null when it
	 *  is fine, and while the server is still working it out. */
	diagnosis: RoleDiagnosis | null;
}

/** Tells the server which button of "Artemis' analysis" was clicked; it only
 *  writes a log line. Fire-and-forget: `keepalive` lets it survive the
 *  navigation the click may cause, and a failure is not worth a message. */
export function logDiagnosisClick(assignmentId: number, action: DiagnosisAction): void {
	apiPost(
		"/api/v2/assignment/log_click/",
		{ assignment_id: assignmentId, action },
		{ keepalive: true }
	).catch(() => undefined);
}

export async function getAssignmentLlmQueueStats(
	assignmentIds: number[]
): Promise<AssignmentLlmQueueStat[]> {
	if (assignmentIds.length === 0) {
		return [];
	}

	return (await apiGet(
		`/api/v2/assignment_llm_queue_stats/?assignment_ids=${assignmentIds.join(",")}`
	)) as AssignmentLlmQueueStat[];
}
